<template>
  <div class="fixed bottom-4 right-4 z-50">
    <!-- Chat Toggle Button -->
    <button
      v-if="!isOpen"
      @click="isOpen = true"
      class="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-xl flex items-center justify-center text-white text-2xl hover:scale-110 transition-transform"
      title="Chat with EUCM Assistant"
    >
      🎓
    </button>

    <!-- Chat Window -->
    <div
      v-else
      class="w-96 h-[540px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
    >
      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-xl">
            🎓
          </div>
          <div>
            <h3 class="text-white font-semibold">{{ buddy?.name || 'EUCM Assistant' }}</h3>
            <p class="text-white/80 text-xs">
              {{ isRateLimited ? 'Limit reached' : 'Ask about courses, events & clinic' }}
            </p>
          </div>
        </div>
        <button
          @click="isOpen = false"
          class="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Rate Limit Banner -->
      <div v-if="isRateLimited" class="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 text-center">
        ⚠️ You've reached your {{ rateLimit.limit }} question limit. Please try again later.
      </div>

      <!-- Messages -->
      <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="text-center py-6">
          <div class="text-4xl mb-3">🎓</div>
          <h4 class="font-semibold text-gray-900 mb-1">Welcome to EUCM!</h4>
          <p class="text-gray-600 text-sm">{{ buddy?.welcome_message || 'Ask me about courses, events, our clinic, or Chinese medicine.' }}</p>
        </div>

        <div
          v-for="(message, index) in messages"
          :key="index"
          class="flex"
          :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] rounded-2xl px-4 py-2.5"
            :class="message.role === 'user'
              ? 'bg-blue-600 text-white rounded-br-none'
              : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none shadow-sm'"
          >
            <div class="text-sm whitespace-pre-wrap">{{ message.content }}</div>
            <div
              v-if="message.qaMatch"
              class="text-xs mt-1 text-green-600 font-medium"
            >
              ✓ Direct answer
            </div>
            <div
              class="text-xs mt-1"
              :class="message.role === 'user' ? 'text-blue-200' : 'text-gray-400'"
            >
              {{ formatTime(message.timestamp) }}
            </div>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="flex justify-start">
          <div class="bg-white border border-gray-200 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
            <div class="flex gap-1">
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.1s" />
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s" />
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Prompts -->
      <div v-if="messages.length === 0 && !isRateLimited" class="px-4 pt-2 bg-white border-t border-gray-100">
        <div class="flex gap-2 overflow-x-auto pb-2">
          <button
            v-for="prompt in quickPrompts"
            :key="prompt.id"
            @click="usePrompt(prompt)"
            class="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-full whitespace-nowrap hover:bg-blue-100 transition"
          >
            {{ prompt.text }}
          </button>
        </div>
      </div>

      <!-- Input Area -->
      <div class="p-4 bg-white border-t border-gray-200">
        <div class="flex gap-2">
          <input
            v-model="inputMessage"
            @keyup.enter="sendMessage"
            :disabled="isRateLimited"
            type="text"
            :placeholder="isRateLimited ? 'Limit reached — try again later' : 'Ask me anything...'"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-400"
          />
          <button
            @click="sendMessage"
            :disabled="!inputMessage.trim() || isTyping || isRateLimited"
            class="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <div class="flex justify-between items-center mt-1">
          <span class="text-[10px] text-gray-400">
            {{ rateLimit.count > 0 ? `${rateLimit.count}/${rateLimit.limit} questions` : '' }}
          </span>
          <span class="text-[10px] text-gray-400">
            {{ authStore.isAuthenticated ? 'Registered user' : 'Guest' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/api'

const authStore = useAuthStore()

const BUDDY_ID = '00000000-0000-0000-0000-000000000001'
const SESSION_KEY = 'eucm_chat_session_token'

const isOpen = ref(false)
const isTyping = ref(false)
const isRateLimited = ref(false)
const inputMessage = ref('')
const messages = ref<Array<{ role: string; content: string; timestamp: Date; qaMatch?: boolean }>>([])
const messagesContainer = ref<HTMLElement>()
const buddy = ref<any>(null)
const conversationId = ref<string | null>(null)
const sessionToken = ref<string>('')
const rateLimit = ref<{ count: number; limit: number }>({ count: 0, limit: 20 })

function getOrCreateSessionToken(): string {
  const existing = localStorage.getItem(SESSION_KEY)
  if (existing) return existing
  const token = crypto.randomUUID()
  localStorage.setItem(SESSION_KEY, token)
  return token
}

function handleToggleAiSidebar() {
  isOpen.value = !isOpen.value
}

const quickPrompts = [
  { id: 1, text: 'What courses do you offer?' },
  { id: 2, text: 'How much does treatment cost?' },
  { id: 3, text: 'What is acupuncture?' },
  { id: 4, text: 'How do I enroll?' },
]

function formatTime(timestamp: Date): string {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

async function fetchBuddy() {
  try {
    const response = await api.get(`/buddies/${BUDDY_ID}`)
    buddy.value = response.data
  } catch (error) {
    console.error('Failed to fetch buddy:', error)
  }
}

async function sendMessage() {
  if (!inputMessage.value.trim() || isTyping.value || isRateLimited.value) return

  const message = inputMessage.value.trim()
  inputMessage.value = ''

  messages.value.push({
    role: 'user',
    content: message,
    timestamp: new Date()
  })

  scrollToBottom()
  isTyping.value = true

  try {
    const endpoint = authStore.isAuthenticated ? `/buddies/${BUDDY_ID}/chat` : `/buddies/${BUDDY_ID}/chat-public`
    const response = await api.post(endpoint, {
      message,
      conversation_id: conversationId.value,
      session_token: sessionToken.value
    })

    const data = response.data

    // Update rate limit info
    if (data.rate_limit) {
      rateLimit.value = data.rate_limit
      isRateLimited.value = data.rate_limit.count >= data.rate_limit.limit
    }

    // Store session token if returned
    if (data.session_token) {
      sessionToken.value = data.session_token
      localStorage.setItem(SESSION_KEY, data.session_token)
    }

    messages.value.push({
      role: 'assistant',
      content: data.response,
      timestamp: new Date(),
      qaMatch: !!data.qa_match?.matched
    })

    if (data.conversation_id) {
      conversationId.value = data.conversation_id
    }
  } catch (error: any) {
    console.error('Failed to send message:', error)
    const errMsg = error.response?.data?.message || 'Sorry, I am having trouble connecting right now. Please try again or contact us at post@medilinka.dk.'
    messages.value.push({
      role: 'assistant',
      content: errMsg,
      timestamp: new Date()
    })

    // Check if rate limited from error response
    if (error.response?.data?.error === 'RATE_LIMITED') {
      isRateLimited.value = true
      if (error.response?.data?.rate_limit) {
        rateLimit.value = error.response.data.rate_limit
      }
    }
  } finally {
    isTyping.value = false
    scrollToBottom()
  }
}

function usePrompt(prompt: any) {
  inputMessage.value = prompt.text
  sendMessage()
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

onMounted(() => {
  sessionToken.value = getOrCreateSessionToken()
  fetchBuddy()
  window.addEventListener('toggle-ai-sidebar', handleToggleAiSidebar)
})

onUnmounted(() => {
  window.removeEventListener('toggle-ai-sidebar', handleToggleAiSidebar)
})
</script>
